import { useState, useRef } from 'react';
import FileUpload from '../hooks/useFileUpload';
import FileUploadGallery from '../hooks/FileUploadGallery ';
import SingleMediaUpload from '../components/uploader/SingleMediaUpload';
import MultipleMediaUpload from '../components/uploader/MultipleMediaUpload';
import { SelectPicker } from 'rsuite';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import CategoryTreePicker from '../admin/product/CategoryTreePicker';

export const FormGenerator = ({ fields, fieldData, onChange, onSubmit, hTure = false, authToken = null }) => {
  const quillRef = useRef(null);
  const [fileData, setFileData] = useState({}); // Store files for each field
  const [errors, setErrors] = useState({}); // Store validation errors
  const [visibleFields, setVisibleFields] = useState({});

  const validateField = (field, value) => {
    let error = '';
    const { minLength, maxLength, isNumeric, isAlphaNumeric, isAlphaNumericWithSymbols, matchWith } = field;
    const inputs = document.querySelectorAll(`#${field.id}`);

    const markInvalid = (message) => {
      inputs.forEach(input => {
        input.classList.add('is-invalid');
      });

      const form = inputs[0]?.closest('form');
      if (form) {
        const submitButtons = form.querySelectorAll('button[type="submit"], input[type="submit"]');
        submitButtons.forEach(btn => btn.disabled = true);
      }

      error = message;
    };

    const clearInvalid = () => {
      inputs.forEach(input => {
        input.classList.remove('is-invalid');
      });

      const form = inputs[0]?.closest('form');
      if (form) {
        const stillInvalid = form.querySelector('.is-invalid');
        const submitButtons = form.querySelectorAll('button[type="submit"], input[type="submit"]');

        submitButtons.forEach(btn => {
          btn.disabled = !!stillInvalid; // Disable if any field still has 'is-invalid'
        });
      }
    };

    if (!field.required && (!value || (typeof value === 'string' && value.trim() === ''))) {
      inputs.forEach(input => {
        input.classList.remove('is-invalid'); // Remove validation error
        input.classList.remove('input'); // Optional: if you meant to remove 'input' class too
      });
      clearInvalid()
      return '';
    }

    if (field.required && !value) {
      markInvalid(`${field.label || 'Field'} is required.`);
      return error;
    }

    if (minLength && value.length < minLength) {
      markInvalid(`${field.label || 'Field'} must be at least ${minLength} characters.`);
      return error;
    }

    if (maxLength && value.length > maxLength) {
      markInvalid(`${field.label || 'Field'} must be less than ${maxLength} characters.`);
      return error;
    }

    if (isNumeric && isNaN(value)) {
      markInvalid(`${field.label || 'Field'} must be a number.`);
      return error;
    }

    if (matchWith) {
      const matchValue = document.getElementById(matchWith)?.value || '';
      if (value !== matchValue) {
        const matchLabel = matchWith.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        markInvalid(`${field.label || 'Field'} must match ${matchLabel}.`);
        return error;
      }
    }

    if (isAlphaNumeric && !/^(?=.*[a-zA-Z])(?=.*\d)[a-zA-Z0-9]+$/.test(value)) {
      markInvalid(`${field.label || 'Field'} must be alphanumeric (letters and numbers).`);
      return error;
    }

    if (
      isAlphaNumericWithSymbols &&
      !/^(?=.*[a-zA-Z])(?=.*\d)(?=.*[!@#$%^&*()_+={}\[\]:;"'<>,.?\/\\|\-])[a-zA-Z0-9!@#$%^&*()_+={}\[\]:;"'<>,.?\/\\|\-]+$/.test(value)
    ) {
      markInvalid(`${field.label || 'Field'} must include letters, numbers, symbols, and no spaces.`);
      return error;
    }

    inputs.forEach(input => {
      input.classList.remove('is-invalid');
    });

    const form = inputs[0]?.closest('form');
    if (form) {
      const hasErrors = form.querySelector('.is-invalid');
      const submitButtons = form.querySelectorAll('button[type="submit"], input[type="submit"]');
      submitButtons.forEach(btn => btn.disabled = !!hasErrors);
    }

    return '';
  };

  const handleChange = (e) => {
    const { name, checked, value } = e.target;
    const field = fields.find(f => f.id === name);

    let newValue;

    if (field?.type === "switch" || field?.type === "checkbox") {
      newValue = checked ? 1 : 0; // force number
    } else {
      newValue = value;
    }

    const error = validateField(field, newValue);
    setErrors((prevErrors) => ({ ...prevErrors, [name]: error }));
    onChange(name, newValue);
  };

  const handleEditorChange = (value, fieldId) => {
    if (fieldData[fieldId] !== value) {
      const error = validateField(fields.find(f => f.id === fieldId), value);
      setErrors((prevErrors) => ({ ...prevErrors, [fieldId]: error }));
      onChange(fieldId, value);
    }
  };

  const handleGalleryChange = (value, fieldId) => {
    onChange(fieldId, value);
  };

  const handleUploadChange = (fieldId, value) => {
    onChange(fieldId, value);
  };

  const toggleVisibility = (id) => {
    setVisibleFields(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const genFields = (field) => {
    const value = fieldData[field.id] || field.default || "";
    const placeholder = field.placeholder || "";

    return (
      <>
        {field.type === "textarea" && (
          <>
            <textarea
              className="form-control"
              id={field.id}
              name={field.id}
              placeholder={placeholder}
              value={value}
              onChange={handleChange}
              required={field.required}
              readOnly={field.readonly}
              disabled={field.disabled}
              style={{ minHeight: "150px" }}
            />
            {errors[field.id] && <small className="text-danger">{errors[field.id]}</small>}
          </>
        )}

        {field.type === "texteditor" && (
          <>
            {/* Quill Editor */}
            <div className="quill-editor">
              <ReactQuill
                ref={quillRef}
                value={value}
                onChange={(content) => handleEditorChange(content, field.id)}
                theme="snow"
                modules={{
                  toolbar: [
                    [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
                    ['bold', 'italic', 'underline', 'strike'],
                    [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'list': 'check' }],
                    [{ 'indent': '-1' }, { 'indent': '+1' }],
                    [{ 'direction': 'rtl' }],
                    [{ 'color': [] }, { 'background': [] }],
                    [{ 'align': [] }],
                    ['link', 'image', 'video'],
                    ['blockquote'],
                  ],
                }}
              />
            </div>
            {errors[field.id] && <small className="text-danger">{errors[field.id]}</small>}
          </>
        )}

        {field.type === "select" && (
          <CustomSelect
            field={field}
            value={value}
            searchable={field.searchable || false}
            handleChange={onChange}
            errors={errors}
          />
        )}

        {field.type === "selectTree" && (
          <CategoryTreePicker
            id={field.id}
            categoryMap={field.options || []}
            defaultValue={value || ''}
            onChange={onChange}
          />
        )}

        {field.type === "radio" && (
          <div className="form-check">
            {field.options.map((option, index) => (
              <div key={index} className="form-check">
                <input
                  type="radio"
                  className="form-check-input"
                  id={`${field.id}_${option}`}
                  name={field.id}
                  value={option}
                  checked={value === option}
                  onChange={handleChange}
                  required={field.required}
                />
                <label className="form-check-label" htmlFor={`${field.id}_${option}`}>
                  {typeof option === 'string'
                    ? option.charAt(0).toUpperCase() + option.slice(1)
                    : (option.label
                      ? option.label.charAt(0).toUpperCase() + option.label.slice(1)
                      : String(option).charAt(0).toUpperCase() + String(option).slice(1)
                    )
                  }
                </label>
              </div>
            ))}
            {errors[field.id] && <small className="text-danger">{errors[field.id]}</small>}
          </div>
        )}

        {field.type === "checkbox" && (
          <>
            <input
              type="checkbox"
              className="form-check-input"
              id={field.id}
              name={field.id}
              checked={value == 1}
              onChange={handleChange}
            />
            {errors[field.id] && <small className="text-danger">{errors[field.id]}</small>}
          </>
        )}

        {field.type === "switch" && (
          <div className="form-check form-switch">
            <input
              type="checkbox"
              className="form-check-input"
              id={field.id}
              name={field.id}
              checked={value == 1}
              onChange={handleChange}
            />
            <label className="form-check-label" htmlFor={field.id}>
              {field.label || "Switch"}
            </label>
            {errors[field.id] && <small className="text-danger">{errors[field.id]}</small>}
          </div>
        )}

        {field.type === "mcheckbox" && field.label && (
          <div>
            {field.label.split(",").map((label, index) => {
              const isChecked = value.split(",").includes(label);
              return (
                <div key={index}>
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id={`${field.id}-${label}`}
                    name={field.id}
                    value={label}
                    checked={isChecked}
                    onChange={handleChange}
                  />
                  {label}
                </div>
              );
            })}
            {errors[field.id] && <small className="text-danger">{errors[field.id]}</small>}
          </div>
        )}

        {field.type === "file" && (
          <div className="mb-2">
            <input
              type="file"
              className="form-control form-control-sm"
              id={field.id}
              name={field.id}
              accept={field.accept || "*/*"}
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  const fileSize = file.size;
                  if (fileSize < 100 * 1024) {
                    handleChange({
                      target: { name: field.id, value: null },
                    });
                    alert("File is too small. Minimum size is 100KB.");
                    return;
                  } else if (fileSize > 1 * 1024 * 1024) {
                    handleChange({
                      target: { name: field.id, value: null },
                    });
                    alert("File is too large. Maximum size is 1MB.");
                    return;
                  }
                  handleChange({
                    target: { name: field.id, value: file },
                  });
                }
              }}
              required={field.required}
            />

            {errors[field.id] && (
              <small className="text-danger">{errors[field.id]}</small>
            )}
          </div>
        )}

        {field.type === "file_1" && !field.multiple && (
          <FileUpload
            fileType={field.id}
            defaultId={value}
            defaultUrl={value}
            defaultExt={value}
            onChange={handleGalleryChange}
            authToken={authToken}
            debug={false}
            error={errors[field.id] ? true : false}
            setUrl={true}
          />
        )}

        {field.type === "gallery" && (
          <FileUploadGallery
            field={{ type: field.id }}
            id={field.id}
            defaultFiles={[value]}
            onChange={handleGalleryChange}
            debug={false}
            error={errors[field.id] ? true : false}
            setUrl={true}
          />
        )}

        {field.type === "upload_single" && !field.multiple && (
          <SingleMediaUpload
            name={field.id}
            defaultFile={value}
            onChange={handleUploadChange}
            authToken={authToken}
          />
        )}

        {field.type === "upload_multiple" && (
          <MultipleMediaUpload
            name={field.id}
            defaultFiles={value}
            onChange={handleUploadChange}
            authToken={authToken}
          />
        )}

        {field.type === "tel" && (
          <>
            <div className="input-group input-group-sm">
              {field.prefix && (
                <span className="input-group-text">{field.prefix}</span>
              )}
              <input
                type="tel"
                inputMode="numeric"
                pattern="[0-9]*"
                id={field.id}
                className="form-control"
                name={field.id}
                value={value}
                placeholder={placeholder}
                onChange={(e) => {
                  const val = e.target.value;
                  // Only allow digits and limit to maxLength
                  if (/^\d*$/.test(val) && (!field.maxLength || val.length <= field.maxLength)) {
                    handleChange(e);
                  }
                }}
                required={field.required}
                readOnly={field.readonly}
                disabled={field.disabled}
                maxLength={field.maxLength}
                minLength={field.minLength}
              />
            </div>
            {errors[field.id] && <small className="text-danger">{errors[field.id]}</small>}
          </>
        )}

        {field.type === "password" && (
          <>
            <div className="input-group input-group-sm position-relative">
              <input
                type={visibleFields[field.id] ? "text" : "password"}
                id={field.id}
                className="form-control"
                name={field.id}
                value={value}
                placeholder={placeholder}
                onChange={handleChange}
                required={field.required}
                readOnly={field.readonly}
              />
              <span
                className="input-group-text"
                onClick={() => toggleVisibility(field.id)}
                style={{ cursor: "pointer", position: "absolute", right: "1px", top: "50%", transform: "translateY(-50%)", zIndex: 10 }}
              >
                <i className={`bi ${visibleFields[field.id] ? "bi-eye-slash" : "bi-eye"}`}></i>
              </span>
            </div>

            {errors[field.id] && <small className="text-danger">{errors[field.id]}</small>}
          </>
        )}

        {field.type === "number" && (
          <>
            <input
              type={field.type}
              id={field.id}
              className="form-control form-control-sm"
              name={field.id}
              value={value}
              min={0}
              placeholder={placeholder}
              onChange={handleChange}
              required={field.required}
              readOnly={field.readonly}
              step="any"
            />
            {errors[field.id] && <small className="text-danger">{errors[field.id]}</small>}
          </>
        )}

        {(field.type === "text" || field.type === "date" || field.type === "email") && (
          <>
            <input
              type={field.type}
              id={field.id}
              className="form-control form-control-sm"
              name={field.id}
              value={value}
              placeholder={placeholder}
              onChange={(e) => {
                const val = e.target.value;
                if (field.required && field.isNumeric) {
                  if (/^\d*$/.test(val) && (!field.maxLength || val.length <= field.maxLength)) {
                    handleChange(e);
                  }
                } else {
                  handleChange(e);
                }
              }}
              required={field.required}
              readOnly={field.readonly}
              disabled={field.disabled}
              maxLength={field.maxLength}
              minLength={field.minLength}
            />
            {errors[field.id] && <small className="text-danger">{errors[field.id]}</small>}
          </>
        )}

        {field.type === "heading" && (
          <h3 className={`mb-2 ${field.textClass || ''}`}>
            {field.text}
          </h3>
        )}

        {/* Description */}
        {field.desc && <p className="small description mb-2 text-muted fst-italic fw-medium" style={{ fontSize: '11px' }}>{field.desc} <i className='bi bi-info-circle'></i> </p>}
      </>
    );
  };

  return (
    <>
      {fields.map((field) => {
        const value = fieldData[field.id] || field.default || "";
        const sectionClass = field.section?.replace(/\s+/g, "-") || "";

        return (
          <div key={field.id} className={`mb-2 ${field.id} ${sectionClass} ${field.class} ${hTure ? 'row' : ''}`}>
            {/* Label */}
            {field.label && field.type !== "checkbox" && field.type !== "mcheckbox" && (
              <label htmlFor={field.id} className={`form-label small mb-1 ${hTure ? 'col-sm-3' : ''}`}>
                {field.label} {field.required ? <span className="text-danger">*</span> : <span><i>(optional)</i></span>}
              </label>
            )}

            {/* Conditional Wrapper */}
            {hTure ? (
              <div className="col-sm-9">
                {genFields(field)}
              </div>
            ) : (
              genFields(field)
            )}

            {field.afterContent && (
              <div dangerouslySetInnerHTML={{ __html: field.afterContent }} />
            )}

            {/* Label */}
            {field.label && field.type === "checkbox" && (
              <label htmlFor={field.id} className={`form-label small mb-1 ms-2 ${hTure ? 'col-sm-3' : ''}`}>
                {field.label} {field.required ? <span className="text-danger">*</span> : <span><i>(optional)</i></span>}
              </label>
            )}
          </div>
        );
      })}
    </>
  );
};

const CustomSelect = ({ field, value, searchable = false, handleChange, errors }) => {
  const options = Object.entries(field.options).map(([key, label]) => ({
    value: key.toString(),  // Ensure string keys
    label: label,
  }));

  const handleSelectChange = (val) => {
    handleChange(field.id, val);
  };

  return (
    <>
      <SelectPicker
        data={options}
        value={value?.toString() || null}
        onChange={handleSelectChange}
        searchable={searchable}
        required={field.required}
        placeholder="Select an option"
        style={{ width: '100%' }}
      />
      {errors[field.id] && <small className="text-danger">{errors[field.id]}</small>}
    </>
  );
};
